<template>
    <div>
        <b-datatable
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                v-if="can('api.v1.pharmacy-groups.store') && can('pharmacy-groups.create')"
                                color="primary"
                                icon
                                v-bind="attrs"
                                :href="createItemUrl()"
                                v-on="on"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </template>
                        <span> {{ t('pharmacy-groups::button.create') }} </span>
                    </v-tooltip>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.group_name"
                        :label="t('pharmacy-groups::label.group_name')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('pharmacy-groups::label.dms_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :readonly="!(can('api.v1.pharmacy-groups.store') && can('pharmacy-groups.create'))"
                        :disabled="!(can('api.v1.pharmacy-groups.store') && can('pharmacy-groups.create'))"
                        :items="filtersOptions.is_registered"
                        :label="t('consultants::label.registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.pharmacy_sites`]="{ item }">
                <span v-for="(value, key) in item.pharmacy_sites" :key="key">
                    {{ value.site_name }}
                </span>
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.group_name`]="{ item }">
                <div v-if="can('pharmacy-groups.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.group_name }}
                </div>
                <div v-else>
                    {{ item.group_name }}
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('pharmacy-groups.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.pharmacy-groups.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-groups::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="!can('api.v1.pharmacy-groups.update') && can('api.v1.pharmacy-groups.show')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-groups::button.details') }}</span>
                    </v-tooltip>
                    <b-delete
                        v-if="can('api.v1.pharmacy-groups.destroy')"
                        :path="deleteItemUrl(item)"
                        :hide-true-btn="!!item.count_pharmacy_sites"
                        :text="
                            !!item.count_pharmacy_sites
                                ? t('pharmacy-groups::confirmation.have_pharmacy_sites_content')
                                : t('default::confirmation.deletion')
                        "
                        :title="
                            !!item.count_pharmacy_sites
                                ? t('pharmacy-groups::confirmation.have_pharmacy_sites_title')
                                : t('default::confirmation.general')
                        "
                        icon="mdi-trash-can"
                        @deleted="triggerDeleted"
                    >
                    </b-delete>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            initialFilters: {
                is_registered: 1,
            },
            hiddenColumns: ['id', 'surname'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('pharmacy-groups::label.group_name'),
                    value: 'group_name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('pharmacy-groups::label.registered'),
                    value: 'is_registered',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('pharmacy-groups::label.dms_id'),
                    value: 'dms_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('pharmacy-groups::label.pharmacy_sites'),
                    value: 'pharmacy_sites',
                    sortable: false,
                    width: 140,
                },
                {
                    text: t('pharmacy-groups::label.chief_pharmacist_name'),
                    value: 'chief_pharmacist_name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'count_pharmacy_sites',
                    width: 180,
                    align: 'end',
                },
            ],
            filtersOptions: {
                is_registered: [
                    {
                        text: t('default::options.all'),
                        value: null,
                    },
                    {
                        text: t('default::options.yes'),
                        value: 1,
                    },
                    {
                        text: t('default::options.no'),
                        value: 0,
                    },
                ],
            },
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('pharmacy-groups.export-record-as-pdf', { pharmacy_group: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        deleteItemUrl(item) {
            return route('api.v1.pharmacy-groups.destroy', { pharmacy_group: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify(
                'info',
                t('default::notification.success'),
                t('pharmacy-groups::notification.pharmacy_groups.deleted')
            );
        },
        itemUrl(item) {
            return route('pharmacy-groups.get', { id: item.id });
        },
        editItemUrl(item) {
            return route('pharmacy-groups.get', { id: item.id });
        },
        createItemUrl() {
            return route('pharmacy-groups.create');
        },
    },
};
</script>
